// src/components/EstimationSummary.jsx
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
  Title
} from "chart.js";
import { API_ORIGIN, authFetch, api as axios } from "../config/apiConfig";
import { ROUTES } from "../config/routes";
ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend, Title);

// FIX: was hardcoded to "/api" -- see the same fix in
// Step1ProjectInfo.jsx; every other page derives this from API_ORIGIN.
const API_BASE = API_ORIGIN;

// Print Report's OpCo logo (see handlePrintReport's coverHtml, further
// down) needs to sit directly on the cover's dark teal background with
// no box behind it and every part of the logo -- regardless of its own
// original colors -- the same solid white. A CSS filter can't do that
// selectively: brightness(0) invert(1) treats every opaque pixel (the
// logo's own background canvas AND its ink) identically, so on a logo
// that isn't transparent-background (most real uploaded ones, e.g. the
// "cluster2" OpCo's) it either shows the logo in its true colors inside
// a visible white chip, or flattens the whole box to one indistinguishable
// white rectangle -- see this function's own use below for the full
// history. This instead decodes the logo onto an offscreen canvas and
// repaints it pixel-by-pixel: bright/near-white pixels (the logo's own
// background, however opaque) become fully transparent, everything else
// (any original hue) becomes solid white, with a soft luminance ramp
// (not a hard cutoff) so anti-aliased edges stay smooth. Resolves null
// (caller falls back to the plain OpCo-name text) if the image can't be
// decoded for any reason.
function toWhiteLogoSilhouette(srcDataUrl) {
  return new Promise((resolve) => {
    if (!srcDataUrl) {
      resolve(null);
      return;
    }
    const img = new Image();
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth || img.width;
        canvas.height = img.naturalHeight || img.height;
        if (!canvas.width || !canvas.height) {
          resolve(null);
          return;
        }
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const px = imageData.data;
        for (let i = 0; i < px.length; i += 4) {
          const r = px[i];
          const g = px[i + 1];
          const b = px[i + 2];
          const a = px[i + 3];
          // Standard perceptual luminance. Pixels at/above ~235 (the
          // logo's own white/near-white canvas) fade fully to
          // transparent; pixels at/below ~200 (actual ink, any color)
          // stay fully opaque; the 35-unit band between is a soft ramp
          // so anti-aliased edges don't turn jagged.
          const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
          const ink = Math.min(1, Math.max(0, (235 - luminance) / 35));
          px[i] = 255;
          px[i + 1] = 255;
          px[i + 2] = 255;
          px[i + 3] = a * ink;
        }
        ctx.putImageData(imageData, 0, 0);
        resolve(canvas.toDataURL("image/png"));
      } catch (err) {
        console.error("Failed to convert OpCo logo to a white silhouette", err);
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = srcDataUrl;
  });
}

// Same 7-step indicator every Cost Estimation wizard page (Step1
// ProjectInfo.jsx, Step2RiskInflation.jsx, ...) shows -- same keys,
// icons, labels, sub-labels. Shown here as an all-green, fully
// clickable "jump to any step" bar (every step is already saved by the
// time a project reaches Summary), NOT as a live progress indicator
// the way the wizard pages use it -- so unlike those pages there's no
// "current"/"disabled" state here.
//
// IMPORTANT: per explicit request, clicking a tab does NOT open the
// real, editable Step1.../BasisOfCost/ElementScopePage1 pages -- it
// opens that step's separate, brand-new READ-ONLY counterpart
// (Step1ProjectInfoReadOnly.jsx, etc. -- see STEP_ROUTES below). This
// guarantees Summary can never be used to accidentally edit a
// "finished" project's data, and the real Cost Estimation step 1-7
// process/pages are never touched by this page at all.
const WIZARD_STEPS = [
  { key: "project", icon: "ti-file-description", label: "Project", sub: "Info & Overview" },
  { key: "budget", icon: "ti-currency-riyal", label: "Budget", sub: "Amount & Summary" },
  { key: "asset", icon: "ti-building", label: "Asset", sub: "Selection & Element" },
  { key: "element", icon: "ti-list-check", label: "Element", sub: "Main & Sub Element" },
  { key: "estimation", icon: "ti-report-money", label: "Estimation", sub: "Amount & Summary" },
  { key: "basis", icon: "ti-clipboard-text", label: "Basis of Cost", sub: "Assumption & Exclusion" },
  { key: "construction", icon: "ti-hammer", label: "Construction", sub: "Scope" },
];

// Each WIZARD_STEPS key -> its READ-ONLY route (Step1ProjectInfoReadOnly
// .jsx/Step2RiskInflationReadOnly.jsx/Step3AssetSelectionReadOnly.jsx/
// Step4SubElementReadOnly.jsx/Step5FinalReadOnly.jsx/
// BasisOfCostReadOnly.jsx/ElementScopePage1ReadOnly.jsx) -- deliberately
// NOT the editable COST_ESTIMATION_STEP1/.../BASIS_OF_COST/
// ELEMENT_SCOPE_PAGE1 routes those same keys used to point to.
const STEP_ROUTES = {
  project: ROUTES.COST_ESTIMATION_STEP1_VIEW,
  budget: ROUTES.COST_ESTIMATION_STEP2_VIEW,
  asset: ROUTES.COST_ESTIMATION_STEP3_VIEW,
  element: ROUTES.COST_ESTIMATION_STEP4_VIEW,
  estimation: ROUTES.COST_ESTIMATION_STEP5_VIEW,
  basis: ROUTES.BASIS_OF_COST_VIEW,
  construction: ROUTES.ELEMENT_SCOPE_PAGE1_VIEW,
};

export default function EstimationSummary() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const projectId = localStorage.getItem("projectID");
  // Per explicit request, "Print Report" needs a real snapshot of the
  // chart as it's actually rendered (colors/values/legend exactly as on
  // screen) -- react-chartjs-2 forwards a ref straight to the underlying
  // Chart.js instance, which exposes toBase64Image() for exactly this.
  // No new npm dependency needed for that part.
  const chartRef = useRef(null);

  // Clicking any step tab below -- keeps projectID set (same key each
  // step page reads on load) and jumps straight to that step's own
  // route. No other flag/behavior change to those pages.
  const goToStep = (stepKey) => {
    if (projectId) localStorage.setItem("projectID", projectId);
    navigate(STEP_ROUTES[stepKey]);
  };

  // Currency symbol for the chart's Y-axis title -- fetched from the
  // Currency Master table (GET /currency, same endpoint Step2RiskInflation
  // .jsx/Step5Final.jsx/Step4SubElement.jsx already use) instead of a
  // hard-coded "SAR"/"﷼" literal, so changing that table's
  // Currency_Symbol value changes what shows here with no code change.
  const [currencySymbol, setCurrencySymbol] = useState("");

  useEffect(() => {
    authFetch(`${API_BASE}/currency`)
      .then((r) => r.json())
      .then((json) => setCurrencySymbol(json?.data?.Currency_Symbol || ""))
      .catch((err) => console.error("Failed to load currency", err));
  }, []);

  // Per explicit request, "Print Report" now includes Project Overview /
  // Basis of Cost / Elemental Construction Scope / Other Scope sections
  // -- none of that data was previously loaded on this page. Rather than
  // add new backend endpoints, this reuses the exact same three GET
  // endpoints the wizard's own read-only view pages already call for the
  // same fields:
  //  - GET /projects?search=<projectId> -- same endpoint ProjectList.jsx
  //    uses, returns class/estimated_by/estimation_date plus the
  //    Project Introduction/Financial Overview/Inflation Forecast/
  //    Contingency Summary/Risk/Opportunity free-text fields
  //    (Step1ProjectInfoReadOnly.jsx's intro/financial/inflation/
  //    contingency/risks/opportunities). Filtered client-side by exact
  //    projectid match since /projects' `search` param does a LIKE match
  //    across several columns, not an exact-id lookup.
  //  - GET /basis_of_cost_api?Projectid= -- same call
  //    BasisOfCostReadOnly.jsx makes, returns assumptions/exclusions plus
  //    the Drawing Name/No/URL rows.
  //  - GET /element_scope?Projectid= -- same call
  //    ElementScopePage1ReadOnly.jsx makes, returns the 9 Construction
  //    Scope fields (Facilitating Works, Substructure, ...) and the 7
  //    Other Scope fields (Main Contractor's Preliminaries, Risk
  //    Allowances, Inflation, VAT Assessment, ...).
  // All three are best-effort: a project that never reached that step
  // simply has empty fields here, and the print report skips any section
  // with nothing real to show rather than displaying blank boxes.
  const [projectExtra, setProjectExtra] = useState(null);
  const [basisData, setBasisData] = useState(null);
  const [elementScopeData, setElementScopeData] = useState(null);
  // Every saved Sub Element row for this project (project_sub_elements),
  // per explicit request: shown as its own table on the print report's
  // Basis of Cost page, right before the Drawings table.
  const [subElementsData, setSubElementsData] = useState([]);

  useEffect(() => {
    if (!projectId) return;
    authFetch(`${API_BASE}/projects?search=${encodeURIComponent(projectId)}&pageSize=20`)
      .then((r) => r.json())
      .then((json) => {
        const rows = json?.data || [];
        const match = rows.find((r) => String(r.projectid) === String(projectId)) || rows[0] || null;
        setProjectExtra(match);
      })
      .catch((err) => console.error("Failed to load project overview fields", err));

    authFetch(`${API_BASE}/basis_of_cost_api?Projectid=${encodeURIComponent(projectId)}`)
      .then((r) => r.json())
      .then((json) => {
        if (json?.success && json?.found) {
          // Match each uploaded file to its Drawing Name/No row, same
          // logic as BasisOfCostReadOnly.jsx -- lets the print report
          // know which row(s) have an attached drawing file to embed.
          const items = Array.isArray(json.items) ? json.items.map((it) => ({ ...it })) : [];
          if (Array.isArray(json.files) && json.files.length) {
            const remainingFiles = [...json.files];
            items.forEach((row) => {
              const idx = remainingFiles.findIndex(
                (f) =>
                  (f.drawingName || "").trim() === (row.drawingName || "").trim() &&
                  (f.drawingNo || "").trim() === (row.drawingNo || "").trim()
              );
              if (idx !== -1) {
                const f = remainingFiles.splice(idx, 1)[0];
                row.attachedFile = { basisId: json.basis_id, filename: f.filename };
              }
            });
          }
          setBasisData({ ...json, items });
        }
      })
      .catch((err) => console.error("Failed to load basis of cost", err));

    authFetch(`${API_BASE}/element_scope?Projectid=${encodeURIComponent(projectId)}`)
      .then((r) => r.json())
      .then((json) => {
        if (json?.success && json?.data) setElementScopeData(json.data);
      })
      .catch((err) => console.error("Failed to load element scope", err));

    authFetch(`${API_BASE}/get_project_sub_elements?project_id=${encodeURIComponent(projectId)}`)
      .then((r) => r.json())
      .then((json) => {
        if (json?.success && Array.isArray(json.data)) setSubElementsData(json.data);
      })
      .catch((err) => console.error("Failed to load sub elements", err));
  }, [projectId]);

  useEffect(() => {
    if (!projectId) {
      setError("Missing projectID in localStorage");
      return;
    }
    authFetch(`${API_BASE}/get_project_estimations_api?projectId=${encodeURIComponent(projectId)}`)
      .then((r) => r.json())
      .then((json) => {
        if (!json.success) throw new Error(json.error || "Failed to fetch");
        setData(json);
      })
      .catch((err) => {
        console.error(err);
        setError(err.message || "Error fetching summary");
      });
  }, [projectId]);

  if (error) return <div className="alert alert-danger">Error: {error}</div>;
  if (!data) return <div>Loading...</div>;

  // percentKey is only set for the 7 "factor" categories that are actually
  // entered/stored as a rate (Prelims_Percent, Risk_Percent, etc., from
  // Cost_Budget_Estimation -- see app.py's /get_project_estimations_api).
  // Construction/Design/Supervision/Other Project are flat amounts with no
  // associated percentage, so percentKey stays null for those.
  const categories = [
    // Per request, display label changed from "Construction" to "Execution"
    // -- key stays "Construction" since that's the raw field name the
    // backend still sends (matches the same rename on the Dashboard's
    // Executive Summary tab).
    { key: "Construction", label: "Execution", percentKey: null },
    { key: "Design", label: "Design", percentKey: null },
    { key: "Supervision", label: "Supervision", percentKey: null },
    { key: "Other_Project", label: "Other Project", percentKey: null },
    { key: "Prelims_Amount", label: "Prelims", percentKey: "Prelims_Percent" },
    { key: "Risk_Amount", label: "Risk", percentKey: "Risk_Percent" },
    { key: "Inflation_Amount", label: "Inflation", percentKey: "Inflation_Percent" },
    { key: "Contingency_Amount", label: "Contingency", percentKey: "Contingency_Percent" },
    { key: "Location_Amount", label: "Location", percentKey: "Location_Percent" },
    { key: "Tender_Amount", label: "Tender", percentKey: "Tender_Percent" },
    { key: "Escalation_Amount", label: "Escalation", percentKey: "Escalation_Percent" }
  ];

  const budgetSeries = categories.map(c => Number(data.budget[c.key] || 0));
  const estimatedSeries = categories.map(c => Number(data.estimated[c.key] || 0));
  const movementSeries = budgetSeries.map((b, i) => Number(estimatedSeries[i] - b));

  // Percentage rate behind each factor column, for the Budget Cost and
  // Estimated Cost rows -- null (rendered as nothing) for the 4 columns
  // that don't have one.
  const budgetPercentSeries = categories.map(c =>
    c.percentKey ? Number(data.budget[c.percentKey] || 0) : null
  );
  const estimatedPercentSeries = categories.map(c =>
    c.percentKey ? Number(data.estimated[c.percentKey] || 0) : null
  );

  const budgetTotal = Number(data.budget.Total_Sum || 0);
  const estimatedTotal = Number(data.estimated.Total_Sum || 0);
  const movement = estimatedTotal - budgetTotal;

  // Validated categorical triad (dataviz skill: scripts/validate_palette.js
  // "#2a78d6,#1baf7a,#e34948" --mode light -- ALL CHECKS PASS: lightness
  // band, chroma floor, CVD adjacent-pair separation, and normal-vision
  // floor all clear; the one WARN (aqua sits under 3:1 contrast on a light
  // surface) is mitigated by the data table below, which is always
  // rendered -- satisfying the "relief rule" instead of relying on the bar
  // color alone. Swapped in for the previous ad-hoc hex values, and reused
  // for the color-dot legend in the table further down so both stay in sync.
  const COLOR_BUDGET = "#2a78d6";
  const COLOR_ESTIMATED = "#1baf7a";
  const COLOR_MOVEMENT = "#e34948";

  // Subtle top-to-bottom gradient per series instead of a flat fill -- a
  // common "premium dashboard" touch that still reads as the same series
  // color (same hue, just lit from the top) rather than introducing new
  // hues. Falls back to the flat color while the canvas context isn't
  // ready yet (Chart.js calls this during the very first layout pass).
  const barGradient = (hex) => (ctx) => {
    const { chart } = ctx;
    const { chartArea } = chart;
    if (!chartArea) return hex;
    const g = chart.ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
    g.addColorStop(0, hex);
    g.addColorStop(1, `${hex}b3`); // ~70% opacity at the base
    return g;
  };

  const chartData = {
    labels: categories.map(c => c.label),
    datasets: [
      {
        // Per request, "Budget Cost" is displayed as "Proposed Cost"
        // throughout this page (KPI tile, chart legend/dataset label,
        // chart title, table row) -- matches the "Proposed Cost" naming
        // already used on Step2RiskInflation.jsx. Internal variable/DB
        // names (budgetSeries, budgetTotal, Cost_Budget_Estimation, etc.)
        // are left as-is -- only the user-facing text changed.
        label: "Proposed Cost",
        data: budgetSeries,
        backgroundColor: barGradient(COLOR_BUDGET),
        hoverBackgroundColor: COLOR_BUDGET,
        maxBarThickness: 22
      },
      {
        label: "Estimated Cost",
        data: estimatedSeries,
        backgroundColor: barGradient(COLOR_ESTIMATED),
        hoverBackgroundColor: COLOR_ESTIMATED,
        maxBarThickness: 22
      }
      // Per explicit request, the "Movement" bar/dataset (and, since
      // Chart.js builds its legend straight from chartData.datasets, its
      // legend entry too) is removed from this chart -- Movement still
      // shows in the data table below unchanged, this only affects the
      // bar chart itself.
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    layout: { padding: { top: 8 } },
    plugins: {
      legend: {
        position: "top",
        // Per explicit request, centered above the chart (was "start",
        // i.e. left-aligned) -- Chart.js's legend.align accepts
        // "start"/"center"/"end" for a "top" position.
        align: "center",
        labels: { usePointStyle: true, pointStyle: "circle", boxWidth: 12, boxHeight: 12, padding: 16, font: { size: 16 } }
      },
      title: { display: false },
      tooltip: {
        backgroundColor: "#124d52",
        titleFont: { size: 15, weight: "600" },
        bodyFont: { size: 15 },
        padding: 10,
        cornerRadius: 6,
        displayColors: true,
        boxPadding: 4,
        callbacks: {
          // Per request, matches the "M" suffix convention used by the KPI
          // tiles/table (fmtM, defined below) instead of the raw
          // full-precision number this tooltip used to show.
          label: (item) => ` ${item.dataset.label}: ${fmtM(item.raw)}`
        }
      }
    },
    scales: {
      x: {
        // Per explicit request (same as Step2RiskInflation.jsx/
        // Step5Final.jsx's charts): X axis titled "Cost Breakdown".
        title: { display: true, text: "Cost Breakdown", font: { size: 17, weight: "bold" }, color: "#52514e" },
        ticks: { maxRotation: 0, autoSkip: true, font: { size: 15, weight: "bold" }, color: "#52514e" },
        grid: { display: false },
        border: { color: "#c3c2b7" },
        // A touch more air between grouped bars and between categories --
        // was Chart.js's default (tight, no breathing room).
        barPercentage: 0.85,
        categoryPercentage: 0.7
      },
      y: {
        // Per explicit request: Y axis titled "Cost (<symbol>)", using the
        // same dynamic currencySymbol (Currency Master table) the other
        // Cost Summary charts already use.
        // Per request, bumped up from 12/10 -- the Y-axis title and tick
        // labels were rendering too small/cramped once fmtM's longer
        // "...M" strings replaced the plain numbers here.
        title: { display: true, text: `Cost (${currencySymbol})`, font: { size: 18, weight: "bold" }, color: "#52514e" },
        ticks: {
          // Per explicit request, the axis itself now shows the value
          // actually converted to millions (e.g. 2,000,000,000 -> "2,000 M")
          // instead of fmtM's old behavior of just appending "M" to the raw
          // number (which is what the tooltip/KPI tiles/table below still
          // do, unchanged, since only the axis was asked for here).
          callback: (val) => fmtAxisMillions(val),
          font: { size: 15, weight: "bold" },
          color: "#898781"
        },
        // Hairline, one-step-off-surface gray -- recessive, never the
        // loudest thing on the chart (see dataviz skill's mark specs).
        grid: { color: "#e1e0d9" },
        border: { display: false },
        // Without this, Chart.js scales the axis right up to the tallest
        // bar's exact value, so the biggest column (e.g. Estimated Cost
        // under Construction) visually touches the plot area's top edge.
        // `grace` pads the axis range by 10% of the data's span above
        // (and below) the actual min/max, same fix as DashboardExecutiveSummary.jsx.
        grace: "10%"
      }
    },
    interaction: { mode: "index", intersect: false },
    // 4px rounded data-end, square at the baseline: borderSkipped "start"
    // (Chart.js's own default) automatically squares off whichever edge
    // sits at zero and rounds the far end, which is what keeps Movement's
    // negative bars rounded correctly at their (downward) tip instead of
    // rounding the wrong end.
    elements: { bar: { borderRadius: 4, borderSkipped: "start" } }
  };

  const fmt = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  // FIX: this used to just append the letter "M" to the full, undivided
  // number (e.g. 1,360,000,000 -> "1,360,000,000.00M"), which only ever
  // looked like millions because of the label, not the value. Per
  // explicit request (KPI tiles / tooltip / table, matching the Y-axis
  // fix above), the value itself is now actually divided by 1,000,000
  // first, with a space before the unit (e.g. 1,360,000,000 -> "1,360.00
  // M") -- same 2-decimal precision as before, just on the real millions
  // figure instead of the raw one.
  const fmtM = (n) => `${fmt(Number(n || 0) / 1000000)} M`;
  const fmtPct = (n) => `${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
  // Y-axis tick labels: same millions conversion as fmtM, but rounded to
  // a whole number (no decimals) since the axis only needs coarse
  // gridline labels, e.g. 2,000,000,000 -> "2,000 M".
  const fmtAxisMillions = (n) =>
    `${(Number(n || 0) / 1000000).toLocaleString(undefined, { maximumFractionDigits: 0 })} M`;

  // Duration_Master's actual seed values (see duration_master_table.sql)
  // are "12 Months", "18 Months", ... "60 Months (5 Years)" -- the exact
  // text Step1's dropdown saves and Step2 shows read-only. Per request,
  // this page instead shows the years-only form (e.g. "2 Years" for the
  // saved "24 Months"). The one seed value that already carries a
  // parenthetical years hint ("60 Months (5 Years)") uses that hint
  // directly rather than re-deriving it, so every tile ends up in the
  // same plain "N Years" shape regardless of which of the two source
  // formats it started from.
  const formatDurationYears = (raw) => {
    if (raw === null || raw === undefined || raw === "") return "—";
    const str = String(raw).trim();
    if (!str) return "—";
    const parenYearsMatch = str.match(/\((\d+(\.\d+)?)\s*years?\)/i);
    if (parenYearsMatch) {
      const y = Number(parenYearsMatch[1]);
      return `${y} Year${y === 1 ? "" : "s"}`;
    }
    if (/year/i.test(str) && !/month/i.test(str)) return str;
    const match = str.match(/(\d+(\.\d+)?)/);
    if (!match) return str;
    const months = Number(match[1]);
    if (!months) return str;
    const years = months / 12;
    const roundedYears = Number.isInteger(years) ? years : Math.round(years * 10) / 10;
    return `${roundedYears} Year${roundedYears === 1 ? "" : "s"}`;
  };

  // Same header/KPI-card look as the theme's executive-summary.html, but
  // the theme's own chart+table area is just a static demo image -- this
  // keeps the real, live Chart.js bar chart and data table (driven by
  // /get_project_estimations_api) instead of flattening to that image.

  // "Print Report" -- per the uploaded PDF template, now the full set of
  // report pages: cover, Executive Summary, Project Overview, Basis of
  // Cost, Elemental Construction Scope, Other Scope, and a closing back
  // page. Same dependency-free approach ProjectList.jsx's "Export as
  // PDF" already uses: write a print-formatted document into a new tab
  // and call window.print() -- the browser's own "Save as PDF"
  // destination in the print dialog is what produces the actual PDF
  // file, no PDF library needed. Per explicit request, every narrative
  // section pulls from the SAME free-text fields the wizard's own
  // read-only view pages already show (projectExtra/basisData/
  // elementScopeData, fetched above) -- a project that never reached a
  // given step just has empty fields there, so that whole section/page
  // is skipped rather than showing a blank box or "Lorem Ipsum".
  const handlePrintReport = async () => {
    // FIX: window.open() used to be called at the very end of this
    // function, after the drawing-file Promise.all below (a real network
    // round trip). Opening a popup that late -- well after the click
    // handler's synchronous "user activation" window has closed -- is
    // exactly what several browsers' popup blockers key on: instead of
    // outright refusing it (which would have hit the `if (!win) alert(...)`
    // guard), they let window.open() return a real window handle but treat
    // it as untrusted/backgrounded, so it shows up as a blank tab that
    // never actually receives the document.write() below and can leave
    // the tab (and sometimes the opener) looking unresponsive until it's
    // closed. Opening it here, synchronously, in the very first tick of
    // the click handler -- before any await -- keeps it inside the
    // trusted user-gesture window every browser honors, so the write/
    // print at the end of this function actually lands in it.
    const win = window.open("", "_blank");
    if (!win) {
      alert("Please allow pop-ups for this site to print the report.");
      return;
    }
    // Immediate placeholder so the new tab never just looks blank/frozen
    // while the drawing-file fetch below is in flight -- document.open()
    // right before the real write() further down clears this out first,
    // so the two writes don't get jumbled together into one document.
    win.document.write(
      `<!doctype html><title>Cost Estimation Report</title><body style="font-family:sans-serif;padding:60px;color:#666;">Generating report&hellip;</body>`
    );

    const chartImg =
      chartRef.current && typeof chartRef.current.toBase64Image === "function"
        ? chartRef.current.toBase64Image()
        : null;

    // Same brand mark Header.jsx's topbar uses -- absolute URL (not a bare
    // "/assets/...") so it resolves correctly from the blank popup window
    // regardless of that window's base URL.
    const logoUrl = `${window.location.origin}/assets/images/logo/CET-Full-icon.png`;
    // Per explicit request, the cover page's top-right corner shows the
    // MATARAT logo (matching the uploaded template's own cover layout,
    // which pairs the brand mark on the left with a client logo on the
    // right) -- this file already exists under public/assets, no upload
    // needed.
    const mataratLogoUrl = `${window.location.origin}/assets/images/logo/matarat_logo.png`;
    // See toWhiteLogoSilhouette's own comment (top of file) for why this
    // replaces the earlier white "chip" background -- per explicit
    // request, the OpCo logo now sits directly on the dark cover with no
    // box behind it.
    const opcoLogoUrl = data.operatingCompanyLogo ? await toWhiteLogoSilhouette(data.operatingCompanyLogo) : null;
    const generatedOn = new Date().toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
    const reportYear = new Date().getFullYear();

    // Free-text fields (Project Introduction, Assumptions, Risk
    // Allowances, ...) are typed by users and must never be trusted as
    // raw HTML -- escaped before going into the popup document.
    const esc = (s) =>
      String(s ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");

    // Most of these free-text fields are plain textareas -- if the user
    // entered several lines, each renders as its own line (see the
    // REVERTED note below); a single line/paragraph renders as plain text.
    // Per request: users often paste multi-level bulleted text (Word-style
    // "•"/"o"/"*" bullets) into these plain-textarea fields; a leading
    // bullet character from the pasted text is stripped before
    // escaping/rendering.
    const stripBulletPrefix = (line) => line.replace(/^(?:[•*]|o)\s+/, "");
    // REVERTED, per follow-up request ("dont start any no 1,2,3,4,5,6",
    // then "start with . dot", then "remove . strart dot"): these
    // multi-line fields get no auto-added marker at all now -- each line
    // just renders as its own plain line, with only whatever the user
    // actually typed.
    const renderTextBlock = (text) => {
      const t = (text || "").toString().trim();
      if (!t) return "";
      const lines = t.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
      if (lines.length > 1) {
        return `<div class="text-list">${lines.map((l) => `<div class="text-line">${esc(stripBulletPrefix(l))}</div>`).join("")}</div>`;
      }
      return `<p>${esc(stripBulletPrefix(t))}</p>`;
    };
    const section = (label, text) => {
      const html = renderTextBlock(text);
      return html ? `<div class="subsection"><h3>${esc(label)}</h3>${html}</div>` : "";
    };

    // "Project Introduction" is now edited with RichTextEditor.jsx (a
    // contentEditable field), so its saved value is real HTML
    // (bold/italic/underline/lists), not plain text -- running it
    // through renderTextBlock()/esc() above would print the literal
    // "<b>" tags instead of bold text. sanitizeRichHtml defensively
    // strips anything unsafe before it's written into the print
    // window's document.
    const sanitizeRichHtml = (html) => {
      if (!html || typeof document === "undefined") return "";
      const container = document.createElement("div");
      container.innerHTML = html;
      container.querySelectorAll("script, style, iframe, object, embed, link, meta").forEach((el) => el.remove());
      // FIX, per follow-up ("underline text have bold but report not
      // showing"): when a run has BOTH bold and underline applied (e.g.
      // a "1. Engineering Assessment and Planning" heading), Chrome's
      // execCommand doesn't always wrap it in a <b>/<strong> tag -- it
      // can instead add font-weight directly as an inline style on the
      // element (alongside the <u> tag, which IS a real tag and was
      // printing fine). The old code stripped the whole "style"
      // attribute except text-align, so that inline
      // font-weight/italic/underline styling was silently thrown away
      // and the text printed underlined-only, not bold. Now a small
      // safe whitelist of formatting properties is kept (in addition to
      // real <b>/<i>/<u> tags, which were never affected).
      const SAFE_STYLE_PROPS = ["textAlign", "fontWeight", "fontStyle", "textDecoration", "textDecorationLine"];
      container.querySelectorAll("*").forEach((el) => {
        const keep = {};
        if (el.style) {
          SAFE_STYLE_PROPS.forEach((prop) => {
            const v = el.style[prop];
            if (v) keep[prop] = v;
          });
        }
        [...el.attributes].forEach((attr) => {
          if (/^on/i.test(attr.name) || attr.name === "style" || attr.name === "href" || attr.name === "src") {
            el.removeAttribute(attr.name);
          }
        });
        Object.keys(keep).forEach((prop) => {
          el.style[prop] = keep[prop];
        });
      });
      return container.innerHTML;
    };
    const richTextSection = (label, html) => {
      const clean = sanitizeRichHtml(html);
      const isEmpty = !clean || clean.replace(/<[^>]+>/g, "").trim() === "";
      return isEmpty ? "" : `<div class="subsection"><h3>${esc(label)}</h3><div class="rich-text-content">${clean}</div></div>`;
    };

    // REVERTED, per follow-up request: Cost Summary table values are back
    // to plain currency figures (fmt) instead of Millions (fmtM) -- the
    // header cells still say "(currencySymbol)", not "(M)", so the
    // values now match their own column headers again.
    const tableRowsHtml = categories
      .map((c, i) => `
        <tr>
          <td class="label-cell">${c.label}</td>
          <td>${fmt(budgetSeries[i])}${c.percentKey ? `<div class="pct">(${fmtPct(budgetPercentSeries[i])})</div>` : ""}</td>
          <td>${fmt(estimatedSeries[i])}${c.percentKey ? `<div class="pct">(${fmtPct(estimatedPercentSeries[i])})</div>` : ""}</td>
          <td>${fmt(movementSeries[i])}</td>
        </tr>
      `)
      .join("");
    // Per request: add a "Total Cost" row below Escalation in the printed
    // report's Cost Summary table only (the on-screen Estimation Summary
    // chart-and-table page is untouched) -- reuses the same
    // budgetTotal/estimatedTotal/movement figures already shown in the
    // tiles above this table.
    const totalRowHtml = `
        <tr class="total-row">
          <td class="label-cell"><b>Total Cost</b></td>
          <td><b>${fmt(budgetTotal)}</b></td>
          <td><b>${fmt(estimatedTotal)}</b></td>
          <td><b>${fmt(movement)}</b></td>
        </tr>
      `;

    // ---- Project Overview page ----
    const estDateDisplay = projectExtra?.estimation_date
      ? new Date(projectExtra.estimation_date).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })
      : "";

    // Per follow-up request, all 6 Project Overview fields are now
    // edited with RichTextEditor.jsx (not just Project Introduction), so
    // all 6 are rendered with richTextSection (real HTML) instead of the
    // plain-text section() helper.
    const overviewSectionsHtml = [
      richTextSection("Project Introduction", projectExtra?.intro),
      richTextSection("Financial Overview", projectExtra?.financial),
      richTextSection("Inflation Forecast", projectExtra?.inflation),
      richTextSection("Contingency Summary", projectExtra?.contingency),
      richTextSection("Risk", projectExtra?.risks),
      richTextSection("Opportunity", projectExtra?.opportunities),
    ].join("");
    const hasOverviewContent = !!overviewSectionsHtml;
    const overviewPageHtml = hasOverviewContent
      ? `
        <div class="page page-with-header">
          <table class="page-repeat">
            <thead><tr><th>
              <div class="summary-header">
                <div class="brand"><img src="${logoUrl}" alt="Cost Estimation Tool" /><span>COST ESTIMATION TOOL</span></div>
                <h2>Project Overview</h2>
              </div>
            </th></tr></thead>
            <tbody><tr><td>
              <div class="info-box">
                <div><b>Project Name</b> ${esc(data.projectName || "—")}</div>
                <div><b>Project ID</b> ${esc(projectId || "—")}</div>
                <div><b>Operating Company</b> ${esc(data.operatingCompany || "—")}</div>
                ${projectExtra?.class ? `<div><b>Estimation Class</b> ${esc(projectExtra.class)}</div>` : ""}
                ${projectExtra?.estimated_by ? `<div><b>Estimated By</b> ${esc(projectExtra.estimated_by)}</div>` : ""}
                ${estDateDisplay ? `<div><b>Estimation Date</b> ${esc(estDateDisplay)}</div>` : ""}
              </div>
              ${overviewSectionsHtml}
            </td></tr></tbody>
          </table>
        </div>
      `
      : "";

    // ---- Basis of Cost page ----
    // Sub Elements table -- every saved project_sub_elements row for this
    // project (Class, Asset Group, Asset Element, Main Element, Sub
    // Element, Description, Unit, Quantity, Rate, Cost), placed
    // immediately before the Drawings table per explicit request ("before
    // drawing add project_sub_elements all subelement information in
    // print report").
    // REVERTED, per follow-up request: Rate and Cost are both back to
    // their normal (non-millions) currency-symbol formatting -- the
    // earlier Cost-in-Millions change (fmtCostM) is undone.
    const subElementRowsHtml = (subElementsData || [])
      .map(
        (it) => `
          <tr>
            <td class="label-cell">${esc(it.asset_class || "—")}</td>
            <td>${esc(it.asset_group || "—")}</td>
            <td>${esc(it.asset_element || "—")}</td>
            <td>${esc(it.main_element || "—")}</td>
            <td>${esc(it.sub_element || "—")}</td>
            <td>${esc(it.description || "—")}</td>
            <td>${esc(it.unit || "—")}</td>
            <td>${fmt(it.quantity)}</td>
            <td>${fmt(it.rate)}</td>
            <td>${fmt(it.cost)}</td>
          </tr>
        `
      )
      .join("");
    const subElementsTotalCost = (subElementsData || []).reduce((sum, it) => sum + Number(it.cost || 0), 0);
    // REVERTED, per follow-up request: the printed report's "Estimated
    // Cost" tile again matches the on-screen `estimatedTotal` (data.
    // estimated.Total_Sum, the saved Flag='E' Cost_Budget_Estimation row)
    // instead of the Sub Elements total below.
    const subElementsSectionHtml = subElementRowsHtml
      ? `<div class="subsection"><h3>Estimated Cost</h3><table class="report-table sub-elements-table"><thead><tr><th>Class</th><th>Asset Group</th><th>Asset Element</th><th>Main Element</th><th>Sub Element</th><th>Description</th><th>Unit</th><th>Quantity</th><th>Rate (${currencySymbol})</th><th>Cost (${currencySymbol})</th></tr></thead><tbody>${subElementRowsHtml}<tr><td class="label-cell" colspan="9" style="text-align:right;">Estimated Cost (${currencySymbol})</td><td>${fmt(subElementsTotalCost)}</td></tr><tr><td class="label-cell" colspan="9" style="text-align:right;">Proposed Cost (${currencySymbol})</td><td>${fmt(budgetSeries[0])}</td></tr></tbody></table></div>`
      : "";

    const drawingRowsHtml = (basisData?.items || [])
      .filter((it) => (it.drawingName || it.DrawingName) || (it.drawingNo || it.DrawingNo) || it.url)
      .map(
        (it) => `
          <tr>
            <td class="label-cell">${esc(it.drawingName || it.DrawingName || "—")}</td>
            <td>${esc(it.drawingNo || it.DrawingNo || "—")}</td>
            <td>${esc(it.url || "—")}</td>
          </tr>
        `
      )
      .join("");

    // Any Drawing row with an uploaded file (matched to basisData.items in
    // the fetch effect above) gets pulled down here as an authenticated
    // blob and embedded after the table -- a plain <img src="..."> can't
    // carry the app's Authorization header, so the file has to be fetched
    // and converted to a base64 data: URL before it can be written into
    // the popup document. Non-image attachments (e.g. a PDF drawing) are
    // skipped rather than shown broken.
    const blobToDataUrl = (blob) =>
      new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    const attachedDrawingRows = (basisData?.items || []).filter((it) => it.attachedFile);
    const drawingImagesHtml = (
      await Promise.all(
        attachedDrawingRows.map(async (it) => {
          try {
            const res = await axios.get(`${API_BASE}/basis_of_cost_api/files`, {
              params: { basis_id: it.attachedFile.basisId, filename: it.attachedFile.filename },
              responseType: "blob",
            });
            const blob = res.data;
            if (!blob) return "";
            const dataUrl = await blobToDataUrl(blob);
            const caption = it.drawingName || it.DrawingName || it.drawingNo || it.DrawingNo || "Drawing";
            // The "Upload (jpg/pdf)" field on Basis of Cost accepts PDFs too
            // (accept=".pdf,image/*") -- a PDF can't be embedded as an
            // <img>, but the file itself is still right here as a data:
            // URL, so it gets a clickable "View Drawing" link instead of
            // being silently dropped (which is what this used to do for
            // anything that wasn't an image, showing the table row with no
            // indication a file was even attached).
            if (blob.type && blob.type.startsWith("image/")) {
              return `<div class="drawing-image"><img src="${dataUrl}" alt="${esc(caption)}" /><div class="drawing-caption">${esc(caption)}</div></div>`;
            }
            return `<div class="drawing-image"><a class="drawing-file-link" href="${dataUrl}" target="_blank" rel="noopener noreferrer">${esc(caption)} &mdash; View Drawing (PDF)</a></div>`;
          } catch (err) {
            console.error("Failed to load drawing file for print report", err);
            return "";
          }
        })
      )
    ).join("");

    const basisSectionsHtml = [
      section("Assumption", basisData?.assumptions),
      section("Exclusion", basisData?.exclusions),
      subElementsSectionHtml,
      drawingRowsHtml
        ? `<div class="subsection"><h3>Drawings</h3><table class="report-table"><thead><tr><th>Drawing Name</th><th>Drawing No</th><th>URL</th></tr></thead><tbody>${drawingRowsHtml}</tbody></table>${drawingImagesHtml}</div>`
        : "",
    ].join("");
    const hasBasisContent = !!basisSectionsHtml;
    const basisPageHtml = hasBasisContent
      ? `
        <div class="page page-with-header">
          <table class="page-repeat">
            <thead><tr><th>
              <div class="summary-header">
                <div class="brand"><img src="${logoUrl}" alt="Cost Estimation Tool" /><span>COST ESTIMATION TOOL</span></div>
                <h2>Basis of Cost</h2>
              </div>
            </th></tr></thead>
            <tbody><tr><td>
              ${basisSectionsHtml}
            </td></tr></tbody>
          </table>
        </div>
      `
      : "";

    // ---- Elemental Construction Scope page ----
    const CONSTRUCTION_SCOPE_FIELDS = [
      ["FacilitatingWorks", "Facilitating Works"],
      ["Substructure", "Substructure"],
      ["Superstructure", "Superstructure"],
      ["InternalFinishes", "Internal Finishes"],
      ["FittingsFurnishingsEquipment", "Fittings, Furnishings & Equipment"],
      ["Services", "Services"],
      ["CompletedBuildingsUnits", "Completed Buildings & Building Units"],
      ["WorksToExistingBuildings", "Works to Existing Buildings"],
      ["ExternalWorks", "External Works"],
    ];
    // Per follow-up request, these 9 fields are now edited with
    // RichTextEditor.jsx on ElementScopePage1.jsx too, so richTextSection
    // (real HTML) replaces the plain-text section() helper here as well.
    const constructionScopeSectionsHtml = CONSTRUCTION_SCOPE_FIELDS.map(([key, label]) =>
      richTextSection(label, elementScopeData?.[key])
    ).join("");
    const hasConstructionScopeContent = !!constructionScopeSectionsHtml;
    const constructionScopePageHtml = hasConstructionScopeContent
      ? `
        <div class="page page-with-header">
          <table class="page-repeat">
            <thead><tr><th>
              <div class="summary-header">
                <div class="brand"><img src="${logoUrl}" alt="Cost Estimation Tool" /><span>COST ESTIMATION TOOL</span></div>
                <h2>Elemental Construction Scope</h2>
              </div>
            </th></tr></thead>
            <tbody><tr><td>
              ${constructionScopeSectionsHtml}
            </td></tr></tbody>
          </table>
        </div>
      `
      : "";

    // ---- Cover page -- per explicit request, matches the uploaded PDF
    // template's own cover: dark teal full page, logo + "Project /
    // Company / Date" lines sitting near the TOP (not centered/spread
    // out), with a diagonal ribbon graphic filling the lower two-thirds
    // -- recreated with plain rotated <div> bands (cover-decor) since
    // the template's graphic is a decorative flourish, not a real asset
    // this app has a file for. The template's own cover pairs its brand
    // mark with the CLIENT's logo (e.g. "jedco") -- this app serves many
    // OpCos, not one fixed client, so that slot shows this project's own
    // Operating Company name instead of a hard-coded partner logo.
    const coverHtml = `
      <div class="page cover">
        <div class="cover-decor">
          <div class="ribbon ribbon-diag"></div>
          <div class="ribbon ribbon-fold"></div>
        </div>
        <div class="cover-content">
          <div class="brand">
            <div class="brand-left">
              <img src="${logoUrl}" alt="Cost Estimation Tool" />
              <span>COST ESTIMATION TOOL</span>
              ${
                opcoLogoUrl
                  ? `<span class="brand-sep">|</span><img class="brand-opco-logo" src="${opcoLogoUrl}" alt="${esc(data.operatingCompany || "OpCo")}" />`
                  : data.operatingCompany
                  ? `<span class="brand-sep">|</span><span class="brand-client">${esc(data.operatingCompany)}</span>`
                  : ""
              }
            </div>
            <img class="matarat-logo" src="${mataratLogoUrl}" alt="MATARAT" />
          </div>
          <hr class="divider" />
          <div class="meta">
            <div><b>Project</b> : ${esc(data.projectName || "—")}</div>
            ${projectExtra?.class ? `<div><b>Estimation</b> : ${esc(projectExtra.class)}</div>` : ""}
            <div><b>Date</b> : ${generatedOn}</div>
          </div>
        </div>
      </div>
    `;

    // ---- Back page -- same dark teal + band treatment as the cover,
    // closing text bottom-left (matching the template's own back page
    // layout). Per explicit request, the copyright line is fixed to
    // "MATARAT" (matching the uploaded template's back page verbatim)
    // rather than the generic/per-OpCo text used before.
    const backPageHtml = `
      <div class="page back-page">
        <div class="cover-decor">
          <div class="ribbon ribbon-diag"></div>
          <div class="ribbon ribbon-fold"></div>
        </div>
        <div class="brand">
          <div class="brand-left">
            <img src="${logoUrl}" alt="Cost Estimation Tool" />
            <span>COST ESTIMATION TOOL</span>
          </div>
          <img class="matarat-logo" src="${mataratLogoUrl}" alt="MATARAT" />
        </div>
        <hr class="divider" />
        <div class="back-footer">
          <span>This report was generated by the Cost Estimation Tool.</span>
          <span>All Rights Reserved &copy; for MATARAT ${reportYear}.</span>
        </div>
      </div>
    `;

    // win was already opened (and given the "Generating report..."
    // placeholder) at the top of this function -- see the FIX comment
    // there for why. document.open() here discards that placeholder
    // document first, so this write() starts a clean document instead of
    // appending onto/alongside the placeholder's own <html>/<body>.
    win.document.open();
    win.document.write(`
      <html>
        <head>
          <title>Cost Estimation Report - ${esc(data.projectName || "Project")}</title>
          <style>
            /* FIX, per request ("add page no all pages"): a 12mm strip
               is reserved at the bottom of every physical printed page
               for a "Page X of Y" footer -- counter(page)/counter(pages)
               are filled in automatically by the browser's print engine
               for every page it lays out, so this repeats correctly no
               matter how many physical pages a section spills across
               (matching the repeating logo+title header fix above).
               This only shows up in the actual Print dialog/PDF output,
               never in the plain on-screen tab -- that's normal for
               @page rules. .page's own min-height is trimmed from 297mm
               to 285mm right below to match the smaller printable area
               this bottom margin leaves (otherwise every page's content
               would overflow by 12mm and spill an extra near-empty
               page). */
            @page {
              size: A4;
              margin: 0 0 12mm 0;
              @bottom-center {
                content: "Page " counter(page) " of " counter(pages);
                font-family: Arial, Helvetica, sans-serif;
                font-size: 9px;
                color: #888;
              }
            }
            /* Without this, browsers drop every background color/image
               (the cover/back page's dark teal, the KPI tiles, table
               header rows, ...) once the actual Print dialog opens --
               they only show up in the on-screen preview -- unless the
               user manually ticks "Background graphics" in that dialog's
               "More settings". Forcing color-adjust here makes them
               print by default with no extra step needed. */
            * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; color-adjust: exact; }
            /* FIX, per request: the floating "Print / Save as PDF" button
               that used to sit here is removed -- the report tab is a
               normal page, so the browser's own print/save controls
               (Ctrl+P / Cmd+P, or the browser's menu) already cover the
               same thing without an extra on-page button. There is still
               no auto-triggered win.print() call anywhere in this report
               -- see the earlier FIX note on win.focus() below for why
               that matters (a forced print dialog can make the main app
               tab look "stuck" while it's modal). .no-print/@media print
               below are kept in case any other floating/no-print element
               is added later. */
            /* FIX, per request ("direct open PDF format"): each .page
               below is a fixed A4-width sheet (210mm) with no centering,
               so on a normal browser tab -- much wider than 210mm -- it
               sat flush against the left edge with a huge blank white gap
               filling the rest of the window, looking broken/unfinished
               rather than like a report. Centering it on a neutral gray
               background (the same "page on a light tray" look Word/
               Google Docs' own print previews use) makes the on-screen
               tab look like a real document instead of a stray column of
               content; the @media print overrides right below strip all
               of that back out for the actual printed/saved-as-PDF pages,
               which only ever see the plain white .page itself, same as
               before this fix. */
            body { font-family: Arial, Helvetica, sans-serif; margin: 0; color: #212529; background: #e9ecef; }
            /* FIX, per request ("this page need fit in one page when
               printing"): the Executive Summary page's own content
               (header + info box + tiles + chart + an 11-row cost table)
               added up to just over one A4 sheet's printable height, so
               it was spilling a couple of rows onto a second printed
               page instead of staying self-contained. Trimmed here and
               in every rule below marked with the same FIX comment --
               slightly tighter page padding, spacing, and table row
               padding, plus a height cap on the chart image -- so the
               whole page reliably fits on one sheet with room to spare. */
            /* FIX, per follow-up (a genuinely blank "Page 3 of 9" showed
               up between the Executive Summary and Project Overview
               pages in the actual PDF): min-height was trimmed from
               297mm to 285mm above to match the new 12mm-shorter
               printable area (12mm is now reserved at the bottom of
               every page for the "Page X of Y" footer), matching it
               exactly. But the Executive Summary page was already
               tuned right up to the OLD limit ("fits with room to
               spare" was only ever a couple of mm, not a lot) -- and
               min-height forces .page to be at least that tall even
               where its real content is shorter, so the tiny bit of
               empty filler space at the very bottom of that forced box
               has to go somewhere once the printable area shrinks: it
               was spilling onto an otherwise-empty next page. Trimming
               another 2mm off as a safety margin (283mm, not 285mm)
               keeps that forced empty filler comfortably inside the
               real printable area so nothing spills over. */
            .page { width: 210mm; min-height: 283mm; padding: 14mm 16mm; position: relative; page-break-after: always; margin: 24px auto; box-shadow: 0 0 16px rgba(0,0,0,0.15); }
            .page:last-child { page-break-after: auto; }
            @media print {
              .no-print { display: none !important; }
              body { background: #fff; }
              .page { margin: 0; box-shadow: none; }
            }

            /* FIX, per follow-up (cover/back-page background showing a
               gap): the 12mm bottom margin added above for the "Page X
               of Y" footer applies to every physical printed page by
               default, including the cover and the closing back-page --
               but those two are a full-bleed dark background + diagonal
               ribbon design, and a browser's print margin is always
               left unpainted (it's reserved for header/footer text
               only), so that 12mm was cutting a plain white strip
               across the bottom of the design. "page: cover-page"
               assigns both to a named page, and the "@page cover-page"
               rule right below (CSS's mechanism for giving specific
               pages their own page setup) gives just these two their
               old zero-margin, no footer, full-bleed treatment back,
               while every other page still gets the 12mm footer margin
               from the plain @page rule above. min-height is restored
               to the full 297mm here too, since these two don't need
               the 12mm trimmed off for a footer they no longer reserve
               space for. */
            .cover, .back-page { page: cover-page; background: #0d3b3c; color: #fff; overflow: hidden; min-height: 297mm; }
            @page cover-page { size: A4; margin: 0; }
            .cover-content { position: relative; z-index: 2; }
            .cover .brand, .back-page .brand { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 10px; position: relative; z-index: 2; }
            .cover .brand-left, .back-page .brand-left { display: flex; align-items: center; gap: 12px; }
            .cover .brand img, .back-page .brand img { height: 40px; width: auto; }
            .cover .brand span, .back-page .brand span { font-size: 20px; font-weight: 700; letter-spacing: 0.5px; }
            .cover .brand-sep { opacity: 0.5; font-weight: 400; }
            .cover .brand-client { font-size: 16px; }
            .cover .divider, .back-page .divider { border: none; border-top: 1px solid rgba(255,255,255,0.35); margin: 0 0 18px; position: relative; z-index: 2; }
            .cover .meta div { font-size: 18px; margin-bottom: 6px; position: relative; z-index: 2; }
            .cover .meta b { font-weight: 700; }
            /* Per request, replaced the plain thin rotated stripes with a
               bolder "folded ribbon" graphic (a diagonal band crossing the
               upper half, plus a wide zigzag/chevron silhouette filling
               the lower half) matching the reference cover page's
               background -- still no image asset, just two clip-path
               shapes on top of the same dark teal base + white overlay
               tones already used elsewhere on this page. */
            .cover-decor { position: absolute; inset: 0; z-index: 1; overflow: hidden; }
            .cover-decor .ribbon { position: absolute; inset: 0; }
            .cover-decor .ribbon-diag { background: rgba(255,255,255,0.09); clip-path: polygon(100% 19%, 100% 31%, 0% 66%, 0% 54%); }
            .cover-decor .ribbon-fold { background: rgba(255,255,255,0.07); clip-path: polygon(0% 100%, 0% 61%, 46% 50%, 63% 74%, 88% 50%, 100% 61%, 100% 100%); }
            .cover .matarat-logo, .back-page .matarat-logo { height: 40px; width: auto; filter: brightness(0) invert(1); }
            /* OpCo logo (from the OpCo table's Logo column, opco_logo_column.sql)
               replaces the plain OpCo-name text next to "COST ESTIMATION" on
               the cover. Was force-recolored via a CSS filter (brightness(0)
               invert(1)), which flattened an opaque-background logo like
               cluster2's into one indistinguishable white rectangle -- then
               a white "chip" background was tried instead, which fixed
               legibility but left a visible box the logo's own creators
               hadn't put there. FIX, per explicit follow-up request ("remove
               background and Cluster/2 same colour"): toWhiteLogoSilhouette
               (top of file) now pre-processes the logo on a canvas before it
               ever reaches this <img> -- its own background is already
               transparent and every mark (any original color) already
               solid white -- so no chip and no filter are needed here at
               all; the logo just sits directly on the dark cover like the
               MATARAT mark beside it. */
            /* Per request: shrink the OpCo logo (e.g. "Cluster2") on the
               print report cover page -- was height:32px/max-width:140px. */
            .cover .brand-opco-logo { height: 22px; width: auto; max-width: 100px; object-fit: contain; }

            .back-page { display: flex; flex-direction: column; justify-content: flex-end; }
            /* FIX, per request: the two closing lines used to stack on
               separate lines -- now on one row, the credit text pushed to
               the right via space-between, matching the request that
               "All Rights Reserved..." sit on the right side. */
            .back-page .back-footer { display: flex; justify-content: space-between; align-items: baseline; gap: 12px; font-size: 12px; opacity: 0.85; position: relative; z-index: 2; }

            /* FIX (fit-one-page): tighter padding/margin -- see .page above. */
            .summary-header { display: flex; align-items: center; justify-content: space-between; border-bottom: 2px solid #124d52; padding-bottom: 8px; margin-bottom: 14px; }
            .summary-header .brand { display: flex; align-items: center; gap: 10px; }
            .summary-header .brand img { height: 28px; width: auto; }
            .summary-header .brand span { font-weight: 700; font-size: 13px; color: #124d52; }
            .summary-header h2 { font-size: 16px; font-weight: 700; color: #124d52; letter-spacing: 0.5px; margin: 0; text-transform: uppercase; }

            /* FIX, per request ("print show the area name with logo
               print all pages"): each content page's .summary-header
               used to sit directly inside the .page div, so when a
               section's content is taller than one physical sheet, the
               overflow spilled onto extra printed pages with no header
               at all -- it only ever showed once, at the top of that
               section. Wrapping the page's content in a <table> with
               the header inside <thead> fixes this: browsers natively
               repeat a table's <thead> on every physical page it spans
               when printing, so the logo + section name now appears on
               every printed sheet, not just the first one. table.
               page-repeat resets the table/cell styling back to plain
               block layout so this is invisible other than the
               repeating header. */
            table.page-repeat { width: 100%; border-collapse: collapse; }
            table.page-repeat > thead > tr > th, table.page-repeat > tbody > tr > td { padding: 0; text-align: left; font-weight: normal; }

            /* FIX, per follow-up ("give some gap in uper site" -- a
               continuation page's header was sitting flush against the
               paper edge with no breathing room, unlike a section's
               first page): a box's own top padding (.page's 14mm) is
               only re-applied on the FIRST physical page a box lands
               on -- browsers don't repeat top/bottom padding on the
               continuation pages of a box that spills across sheets,
               so .page's 14mm top gap was missing on every page after
               the first one for a page. The fix moves that top gap
               onto the *table's* <thead> instead of the outer .page
               div -- page-with-header turns off .page's own top
               padding, and the same 14mm now lives on the repeating
               header cell instead, so it applies identically on every
               physical page (first and every continuation one),
               matching the first page's look everywhere. */
            .page.page-with-header { padding-top: 0; }
            table.page-repeat > thead > tr > th { padding-top: 14mm; }

            /* FIX (fit-one-page): tighter padding/margin -- see .page above. */
            .info-box { background: #f4f5f7; border-radius: 4px; padding: 10px 18px; margin-bottom: 14px; font-size: 13px; }
            .info-box div { margin-bottom: 4px; }
            .info-box b { display: inline-block; width: 160px; }

            /* FIX (fit-one-page): tighter margin/padding -- see .page above. */
            .tiles { display: flex; gap: 10px; margin-bottom: 14px; }
            .tiles .tile { flex: 1; border-radius: 4px; padding: 9px 8px; text-align: center; color: #fff; }
            .tiles .tile .val { font-size: 15px; font-weight: 700; }
            .tiles .tile .lbl { font-size: 10px; font-weight: 600; margin-top: 4px; }

            /* FIX (fit-one-page), then re-FIXED per explicit request ("chart
               showing very small size"): the original 62mm max-height cap
               sat BELOW the chart's own natural width-constrained height
               (~75mm at this page's ~178mm content width and the chart's
               ~2.37:1 aspect ratio) -- so it was the binding constraint,
               shrinking the chart to noticeably less than full page width
               on top of being short. Raised to 100mm, comfortably above
               that ~75mm natural height, so max-width:100% (not this cap)
               is what actually sizes the chart now -- it renders at the
               page's full content width with proportional height, both
               visibly bigger than before. The rest of this page's content
               (info-box/tiles/11-row table) was re-measured to confirm this
               still leaves headroom to fit one printed A4 sheet. */
            .chart-wrap { margin-bottom: 12px; text-align: center; }
            /* FIX (extra safety margin for the blank-page-3 fix above):
               trimmed another 6mm off the chart's own height cap so
               the Executive Summary page has a bit more headroom to
               reliably stay on one sheet even with the smaller
               printable area. */
            .chart-wrap img { max-width: 100%; max-height: 94mm; width: auto; height: auto; }

            table.report-table { width: 100%; border-collapse: collapse; font-size: 11px; margin-bottom: 8px; }
            /* FIX (fit-one-page): shaved 3px off top+bottom row padding --
               multiplied across the table's 11 cost-category rows plus its
               header row, this is the single biggest space saving here. */
            table.report-table th, table.report-table td { border: 1px solid #dcdcdc; padding: 5px 6px; text-align: center; }
            table.report-table th { background: #124d52; color: #fff; font-size: 10.5px; text-transform: uppercase; }
            table.report-table th:first-child, table.report-table td.label-cell { text-align: left; }
            table.report-table td.label-cell { font-weight: 600; }
            /* FIX (fit-one-page): trimmed margin -- see .page above. */
            table.report-table .pct { font-size: 9px; color: #777; margin-top: 1px; }
            /* Per request: "Total Cost" row appended below Escalation in
               the Cost Summary table (print report only) -- a light tint
               plus a heavier top border sets it apart as the summary line. */
            table.report-table tr.total-row td { background: #eef3f2; border-top: 2px solid #124d52; }

            /* Sub Elements table has 10 columns (Class, Asset Group, Asset
               Element, Main Element, Sub Element, Description, Unit,
               Quantity, Rate, Cost) -- tighter font/padding than the
               standard .report-table so it still fits the 210mm page width
               legibly, with description text wrapping instead of
               overflowing its cell. */
            table.report-table.sub-elements-table { table-layout: fixed; }
            table.report-table.sub-elements-table th,
            table.report-table.sub-elements-table td { padding: 4px 4px; word-break: break-word; }

            .subsection { margin-bottom: 16px; }
            .subsection h3 { font-size: 12.5px; font-weight: 700; color: #124d52; margin: 0 0 6px; text-transform: uppercase; letter-spacing: 0.3px; }
            .subsection p { font-size: 12px; line-height: 1.6; margin: 0; color: #333; }
            .subsection div.text-list { margin: 0; font-size: 12px; line-height: 1.6; color: #333; }
            .subsection div.text-list .text-line { margin-bottom: 4px; }
            .subsection .rich-text-content { font-size: 12px; line-height: 1.6; color: #333; white-space: pre-wrap; }
            .subsection .rich-text-content p, .subsection .rich-text-content div { margin: 0 0 6px; }
            .subsection .rich-text-content ul, .subsection .rich-text-content ol { margin: 0 0 6px; padding-left: 20px; }
            .subsection .rich-text-content li { margin: 0 0 2px; }
            /* FIX, per follow-up ("report not showing all rich text"):
               forcing every <b>/<strong> run to display:block (added
               for the numbered-heading request) was too broad -- it
               also broke any bold WORD or PHRASE in the middle of a
               normal sentence onto its own line, fragmenting ordinary
               paragraphs and making mixed plain/bold text look like it
               was missing content. Bold is back to normal inline
               behavior; headings still land on their own line because
               each one is typed as its own paragraph/div in the editor
               (block-level by default already) and white-space:
               pre-wrap above preserves that structure exactly as
               typed, with no need to force every bold run onto its own
               line. */
            .subsection .rich-text-content b, .subsection .rich-text-content strong { font-weight: 700; }

            .drawing-image { margin-top: 10px; text-align: center; }
            .drawing-image img { max-width: 100%; max-height: 450px; border: 1px solid #dcdcdc; padding: 4px; background: #fff; }
            .drawing-image .drawing-caption { font-size: 10.5px; color: #666; margin-top: 4px; }
            .drawing-file-link { display: inline-block; padding: 8px 16px; border: 1px solid #02bc9c; border-radius: 4px; color: #02bc9c; text-decoration: none; font-size: 12px; font-weight: 600; }
          </style>
        </head>
        <body>
          ${coverHtml}

          <div class="page page-with-header">
            <table class="page-repeat">
              <thead><tr><th>
                <div class="summary-header">
                  <div class="brand">
                    <img src="${logoUrl}" alt="Cost Estimation Tool" />
                    <span>COST ESTIMATION TOOL</span>
                  </div>
                  <h2>Executive Summary</h2>
                </div>
              </th></tr></thead>
              <tbody><tr><td>
                <div class="info-box">
                  <div><b>Project Name</b> ${esc(data.projectName || "—")}</div>
                  <div><b>Operating Company</b> ${esc(data.operatingCompany || "—")}</div>
                  <div><b>Project Duration</b> ${esc(formatDurationYears(data.duration))}</div>
                </div>

                <div class="tiles">
                  <div class="tile" style="background:#3d5166"><div class="val">${esc(data.operatingCompany || "—")}</div><div class="lbl">COMPANY NAME</div></div>
                  <div class="tile" style="background:#7d5ba6"><div class="val">${esc(formatDurationYears(data.duration))}</div><div class="lbl">PROJECT DURATION</div></div>
                  <div class="tile" style="background:#147196"><div class="val">${currencySymbol} ${fmtM(budgetTotal)}</div><div class="lbl">PROPOSED COST</div></div>
                  <div class="tile" style="background:#8cc653"><div class="val">${currencySymbol} ${fmtM(estimatedTotal)}</div><div class="lbl">ESTIMATED COST</div></div>
                  <div class="tile" style="background:#c81017"><div class="val">${currencySymbol} ${fmtM(movement)}</div><div class="lbl">VARIANCE</div></div>
                </div>

                <div class="chart-wrap">
                  ${chartImg ? `<img src="${chartImg}" alt="Proposed Vs Estimation chart" />` : ""}
                </div>

                <table class="report-table">
                  <thead>
                    <tr>
                      <th>Cost Summary</th>
                      <th>Proposed Cost (${currencySymbol})</th>
                      <th>Estimated Cost (${currencySymbol})</th>
                      <th>Variance (${currencySymbol})</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${tableRowsHtml}
                    ${totalRowHtml}
                  </tbody>
                </table>
              </td></tr></tbody>
            </table>
          </div>

          ${overviewPageHtml}
          ${basisPageHtml}
          ${constructionScopePageHtml}
          ${backPageHtml}
        </body>
      </html>
    `);
    win.document.close();
    // FIX: win.focus() used to run here, forcing the report tab to the
    // foreground the instant it opened -- per explicit request, the user
    // needs to keep navigating the Cost Estimation Tool itself (sidebar
    // links, other pages) while the report is open, and a stolen-focus
    // tab switch is exactly what got in the way of that: this tab stayed
    // the active/frontmost one until the user manually switched away.
    // Leaving this out means the report opens quietly in a background
    // tab -- this tab never loses focus, so every link and button here
    // keeps working immediately -- and the user can switch to the report
    // tab whenever they actually want to look at or print it.
    //
    // Also no more auto-triggered win.print() here, and no floating
    // "Print / Save as PDF" button either -- the report tab just opens
    // and sits there in the background; printing/saving happens whenever
    // the user chooses, via the browser's own print/save controls
    // (Ctrl+P / Cmd+P, or its menu).
  };

  return (
    <>
      {/* Per request, the "Estimate Cost" button that used to sit on the
          right of this breadcrumb row is removed -- this page is now a
          pure read-only summary, so there's nothing to link there any
          more. The breadcrumb itself is unchanged. */}
      {/* pt-3 added per explicit request: nudges the breadcrumb down a
          little from the very top of the page instead of sitting flush
          against it. */}
      <div className="pt-3 pb-3 mb-4 border-bottom d-flex align-items-center justify-content-between flex-wrap gap-2">
        {/* Was a plain "EXECUTIVE SUMMARY" label with no indication of
            which project this page is showing -- replaced with the same
            "Cost Estimation / Project / {name}" breadcrumb trail every
            wizard step (Step2RiskInflation.jsx, Step3AssetSelection.jsx,
            Step5Final.jsx, ...) already uses, per request. projectName
            comes from the same /get_project_estimations_api call this
            page already makes for its figures (added alongside this
            fix -- that endpoint didn't return a project name before). */}
        <nav aria-label="breadcrumb" className="d-inline-block">
          <span style={{ fontSize: 16, color: "#02bc9c" }}>Cost Estimation</span>
          <span style={{ fontSize: 16, color: "#02bc9c" }} className="mx-2">/</span>
          <span style={{ fontSize: 16, color: "#02bc9c" }}>Project</span>
          <span style={{ fontSize: 16, color: "#02bc9c" }} className="mx-2">/</span>
          <span className="fw-semibold" style={{ fontSize: 16, color: "#02bc9c" }}>
            {data.projectName || "—"}
          </span>
        </nav>

        {/* Per explicit request: "Print Report" button, below the header/
            breadcrumb and above the Construction (wizard step) bar --
            styled after the theme's own PRINT button on
            CET-HTML-Code/executive-summary.html (same #124d52 dark teal,
            printer icon, same button treatment). */}
        <button
          type="button"
          className="btn btn-sm text-white px-3 py-2 fw-semibold d-inline-flex align-items-center gap-2"
          style={{ backgroundColor: "#124d52", borderRadius: 2 }}
          onClick={handlePrintReport}
        >
          <i className="ti ti-printer fs-5" />
          Print Report
        </button>
      </div>

      {/* Step indicator bar -- restored per request as an all-green,
          clickable "jump to any step" bar (see WIZARD_STEPS/STEP_ROUTES
          above). Same markup/icons as each wizard step page's own
          indicator, just always "done" here instead of current/
          disabled, and with an onClick instead of being inert. */}
      <div className="mb-4">
        <div className="progress mb-3" style={{ height: 6 }}>
          <div className="progress-bar wizard-progress-bar" style={{ width: "100%" }} />
        </div>
        <ul className="nav nav-tabs wizard-tabs w-100 row g-2 border-0" role="tablist">
          {WIZARD_STEPS.map((step) => (
            <li className="nav-item col-12 col-lg" key={step.key}>
              <span
                className="nav-link h-100 wizard-item-done"
                role="button"
                tabIndex={0}
                style={{ cursor: "pointer" }}
                onClick={() => goToStep(step.key)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    goToStep(step.key);
                  }
                }}
              >
                <span className="d-flex align-items-center">
                  {step.key === "budget" ? (
                    <svg
                      className="flex-shrink-0"
                      style={{ width: "32px", height: "32px" }}
                      viewBox="0 0 917 1025"
                      fill="currentColor"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path fillRule="evenodd" d="M 916 834 L 570 908 L 550 964 L 542 1001 L 540 1024 L 884 951 L 893 932 L 904 899 L 912 865 Z M 646 54 L 539 144 L 539 473 L 435 496 L 430 1 L 324 89 L 324 518 L 83 570 L 51 682 L 323 629 L 324 767 L 32 830 L 0 946 L 310 881 L 346 863 L 428 746 L 437 604 L 539 584 L 539 803 L 885 730 L 916 614 L 646 670 L 646 561 L 885 509 L 916 397 L 646 449 Z" />
                    </svg>
                  ) : (
                    <i className={`ti ${step.icon} fs-32 flex-shrink-0`} />
                  )}
                  <span className="flex-grow-1 ms-2 text-truncate">
                    <span className="mb-0 lh-base d-block fw-semibold text-body fs-base">{step.label}</span>
                    <span className="fs-xxs mb-0">{step.sub}</span>
                  </span>
                </span>
              </span>
            </li>
          ))}
        </ul>
      </div>

      {/* Per request, a dark-gray separator line between the wizard step
          bar above and the KPI tiles below -- same idea as the
          Dashboard Executive Summary page's own separator
          (DashboardExecutiveSummary.jsx), just with an explicit darker
          border color rather than relying on Bootstrap's own faint
          default <hr>. */}
      <hr className="my-4" style={{ borderTop: "1px solid #6c757d" }} />

      {/* Per request, all 5 tiles now sit in a single row instead of
          wrapping to a second line once OpCo/Duration were added --
          row-cols-lg-5 (Bootstrap's row-cols utility, not the usual
          12-column col-* split, since 5 doesn't divide evenly into 12)
          puts all 5 side by side at laptop/desktop widths, stepping down
          to 3/2/1 per row on md/sm/xs so nothing gets too squeezed on
          smaller screens. */}
      <div className="row row-cols-1 row-cols-sm-2 row-cols-md-3 row-cols-lg-5 g-3 mb-4">
        {/* OpCo and Duration -- per request, added ahead of the Budget
            Cost tile. Both come straight off this page's own
            /get_project_estimations_api response (operatingCompany /
            duration -- see app.py's get_project_estimations, which reads
            them from Cost_Budget_Estimation the same place Step1/Step2
            already save them) keyed to the actual project being viewed,
            so nothing here needs the user to pick anything -- it's
            whatever was selected back in Step 1, shown automatically.
            operatingCompany is already resolved server-side from the
            raw OpCo_ID code (e.g. "OPCO-2") to its real OpCo_Name (e.g.
            "DAMMAM") -- see get_project_estimations_api in app.py. */}
        <div className="col">
          <div className="p-3 text-center text-white rounded h-100 d-flex flex-column justify-content-center" style={{ backgroundColor: "#3d5166" }}>
            <div className="fs-4 fw-bold lh-1 text-truncate" title={data.operatingCompany || "—"}>
              {data.operatingCompany || "—"}
            </div>
            {/* Per request, relabeled from "OpCo" to "Company Name". */}
            <div className="fs-xs fw-semibold mt-1">Company Name</div>
          </div>
        </div>
        <div className="col">
          <div className="p-3 text-center text-white rounded h-100 d-flex flex-column justify-content-center" style={{ backgroundColor: "#7d5ba6" }}>
            <div className="fs-4 fw-bold lh-1">{formatDurationYears(data.duration)}</div>
            {/* Per request, relabeled from "Duration" to "Project Duration". */}
            <div className="fs-xs fw-semibold mt-1">Project Duration</div>
          </div>
        </div>
        {/* Per request, all 5 tiles' value text was bumped from fs-5 back
            up to fs-4 for better legibility, keeping them all at the same
            scale (Company Name/Project Duration tiles bumped alongside
            the 3 cost tiles, not just the cost ones). */}
        <div className="col">
          <div className="p-3 text-center text-white rounded h-100 d-flex flex-column justify-content-center" style={{ backgroundColor: "#147196" }}>
            <div className="fs-4 fw-bold lh-1">{currencySymbol} {fmtM(budgetTotal)}</div>
            <div className="fs-xs fw-semibold mt-1">Proposed Cost</div>
          </div>
        </div>
        <div className="col">
          <div className="p-3 text-center text-white rounded h-100 d-flex flex-column justify-content-center" style={{ backgroundColor: "#8cc653" }}>
            <div className="fs-4 fw-bold lh-1">{currencySymbol} {fmtM(estimatedTotal)}</div>
            <div className="fs-xs fw-semibold mt-1">Estimated Cost</div>
          </div>
        </div>
        <div className="col">
          <div className="p-3 text-center text-white rounded h-100 d-flex flex-column justify-content-center" style={{ backgroundColor: "#c81017" }}>
            <div className="fs-4 fw-bold lh-1">{currencySymbol} {fmtM(movement)}</div>
            <div className="fs-xs fw-semibold mt-1">Variance</div>
          </div>
        </div>
      </div>

      {/* Per request, widened further -- side padding removed entirely
          (was p-3/p-md-4, then px-2/px-md-3) so the canvas (responsive:
          true, fills 100% of this card's content box) now spans the exact
          same full width as the KPI tiles row and data table above/below
          it, instead of sitting inset within its own card padding. Title
          gets its own small side padding (px-2) so "Proposed Vs
          Estimation" doesn't sit flush against the card's rounded edge. */}
      <div className="card border-0 shadow-sm rounded py-3 py-md-4 px-0 mb-4">
        <h6 className="fw-bold mb-3 px-2">Proposed Vs Estimation</h6>
        {/* Per request, enlarged further -- 340px -> 480px -> 640px -> 760px --
            taller chart reads more clearly, especially the smaller bars
            (Risk, Inflation, etc.) next to Execution's much larger one. */}
        <div style={{ height: 760 }}>
          <Bar ref={chartRef} data={chartData} options={chartOptions} />
        </div>
      </div>

      <div className="card border rounded p-3 mb-4">
        <div className="table-responsive">
          <table className="table table-centered align-middle text-center mb-0">
            <thead>
              <tr className="fs-xs fw-bold text-success">
                <th></th>
                {categories.map(c => <th key={c.key}>{c.label}</th>)}
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="text-start fw-semibold">
                  <span className="d-inline-block rounded-circle me-2" style={{ width: 10, height: 10, background: COLOR_BUDGET }} />
                  Proposed Cost
                </td>
                {budgetSeries.map((v, i) => (
                  <td key={i}>
                    {fmtM(v)}
                    {categories[i].percentKey && (
                      <div className="fs-xxs text-muted mt-1">({fmtPct(budgetPercentSeries[i])})</div>
                    )}
                  </td>
                ))}
              </tr>

              <tr>
                <td className="text-start fw-semibold">
                  <span className="d-inline-block rounded-circle me-2" style={{ width: 10, height: 10, background: COLOR_ESTIMATED }} />
                  Estimated Cost
                </td>
                {estimatedSeries.map((v, i) => (
                  <td key={i}>
                    {fmtM(v)}
                    {categories[i].percentKey && (
                      <div className="fs-xxs text-muted mt-1">({fmtPct(estimatedPercentSeries[i])})</div>
                    )}
                  </td>
                ))}
              </tr>

              <tr>
                <td className="text-start fw-semibold">
                  <span className="d-inline-block rounded-circle me-2" style={{ width: 10, height: 10, background: COLOR_MOVEMENT }} />
                  Variance
                </td>
                {movementSeries.map((v, i) => <td key={i}>{fmtM(v)}</td>)}
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}